import * as z from 'zod';
import { restaurantOrderSchema } from './order';
import { timestampSchema } from './common';

// Realtime order events (`shared/common/realtime` on the backend) — what the
// merchant app receives on its `merchant:<id>` Centrifugo channel.
//
// Envelope (MerchantEvent): every order change visible to the merchant is
// published with the FULL order card (`data`, identical JSON to
// `GET /orders/by-restaurant/{id}`) plus what happened:
//
//   {
//     "id": "01a0fc18-…", "op": "create", "type": "created",
//     "vertical": "eats", "occurred_at": "2026-10-02T09:14:20",
//     "entity": "order", "entity_id": "3f6b9c2e-…", "version": 2,
//     "data": { …RestaurantOrder… }
//   }
//
// Client rules (backend contract §6), encoded where noted:
// - `entity` is the discriminator (`type` names the transition and never
//   contains "order"). Column/badge derive ONLY from
//   `data.merchant_order_status`; `type` drives reactions (sound/toast).
// - Delivery is at-least-once: `version` (== `data.order.version`) gates
//   application — an event at or below the last applied number is a duplicate
//   or a late arrival and is dropped WITHOUT reacting. Version numbers skip
//   (invisible changes bump them too); only "newer or not" matters.
// - An UNKNOWN `type` must still parse: it is a plain card update with no
//   reaction, and new types will be added. Hence `type: z.string()` below,
//   not an enum — an enum would fail the whole event on the next new type.

/** Known `type` values. Informational: the schema accepts any string. */
export const EATS_EVENT_TYPES = [
  'created',
  'accepted',
  'courier_assigned',
  'ready',
  'picked_up',
  'delivered',
  'cancelled',
  'pickup_time_changed',
  'cancel_requested',
  'updated',
] as const;
export type EatsEventType = (typeof EATS_EVENT_TYPES)[number];

export const merchantEventSchema = z.object({
  /** Event id (UUID v7) — for logs, decides nothing. */
  id: z.string(),
  /** Kept a string: a future op must not fail the event (same as `type`). */
  op: z.string(),
  /** What happened — reaction-only; unknown values parse (see above). */
  type: z.string(),
  /** Card type in `data`; only eats cards are understood. */
  vertical: z.literal('eats'),
  occurred_at: timestampSchema,
  entity: z.literal('order'),
  /** The order id — always equals `data.order.id`. */
  entity_id: z.string(),
  /** Change number — always equals `data.order.version`; the dedupe key. */
  version: z.number().int().nonnegative(),
  /** Full card, same JSON as the REST read. */
  data: restaurantOrderSchema,
});
export type MerchantEvent = z.infer<typeof merchantEventSchema>;
